import type {
  InputDescription,
  ListConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import { Button, Icon, IconButton, Tooltip } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { translateIfPresent } from "../../../../../../../../../locales/i18n";
import type { ActionCandidate } from "../../../../../../../../../types/candidate";
import { BooleanValue } from "../BooleanValue/BooleanValue";
import { MAX_DEPTH } from "../JsonEditor";
import { JsonObject } from "../JsonObject/JsonObject";
import { PropertyModal } from "../Modal/property-modal";
import { ModalSelectField } from "../Modal/type-select-modal";
import { NumberValue } from "../NumberValue/NumberValue";
import { ReferenceValue } from "../ReferenceValue/ReferenceValue";
import { StringValue } from "../StringValue/StringValue";
import { buttonStyles, ButtonWrapper, Space, VerticalLine } from "../style";
import { getListBodyType } from "../utils/inputs";
import * as S from "./style";

export const ArrayObject = observer<{
  input: ListConfig;
  description: InputDescription;
  action?: ActionCandidate;
  label?: string;
  onClick: () => void;
  onRemove: (index: number) => void;
  onEdit: (index: number) => void;
  onlyStructure?: boolean;
  depth: number;
  index: number;
}>(
  ({
    input,
    label,
    description,
    onClick,
    onlyStructure,
    depth,
    onRemove,
    onEdit,
    index,
    action,
  }) => {
    const { t } = useTranslation();
    const [typeSelectModalOpen, setTypeSelectModalOpen] =
      useState<boolean>(false);
    const [propertyModalOpen, setPropertyModalOpen] = useState<boolean>(false);
    const [propToEdit, setPropToEdit] = useState<ValueConfig_V | undefined>();
    const [editPropIndex, setEditPropIndex] = useState<number | undefined>();

    const addItem = (): void => {
      setTypeSelectModalOpen(true);
    };

    const removeItem = (index: number): void => {
      input.values.splice(index, 1);
      onClick();
    };

    const editItem = (index: number): void => {
      setPropToEdit({
        value: {
          oneofKind: "list",
          list: input,
        },
      });
      setEditPropIndex(index);
      setPropertyModalOpen(true);
      onClick();
    };

    const isAddItemIsPossible =
      !onlyStructure || (onlyStructure && input.values.length < 1);

    return (
      <S.Root>
        <S.Title>
          <span>
            <S.RootIconWrapper>
              <Icon size={"S"} name="array" />
            </S.RootIconWrapper>

            <S.Text>
              {`${label ?? ""}${
                label === undefined ? "" : ":"
              } ${translateIfPresent("caption.JSON Array", "JSON Array")}`}
            </S.Text>
          </span>
          <S.RightItems>
            <S.RightButton>
              <Tooltip
                text={t("automation-form.update-response")}
                placements={["top", "bottom"]}
                padding={2}
                target={
                  <>
                    {depth > 1 && (
                      <IconButton
                        name="edit-thin"
                        size="S"
                        ariaLabel="Edit Button"
                        color="currentColor"
                        hoverColor="currentColor"
                        onClick={() =>
                          index !== undefined && onEdit && onEdit(index)
                        }
                      />
                    )}
                  </>
                }
              />
            </S.RightButton>
            <Space />
            <S.RightButton>
              <Tooltip
                text={t("automation-form.delete-response")}
                placements={["top", "bottom"]}
                padding={2}
                target={
                  <IconButton
                    name="trash-thin"
                    size="S"
                    ariaLabel="Remove Button"
                    color="currentColor"
                    hoverColor="currentColor"
                    onClick={() => onRemove(index)}
                  />
                }
              />
            </S.RightButton>
          </S.RightItems>
        </S.Title>
        <VerticalLine>
          {input?.values?.flatMap((item, index) => {
            if (item.values.every((v) => v.value.oneofKind === "keyValue")) {
              return (
                <JsonObject
                  key={index}
                  label={index}
                  description={description}
                  onlyStructure={onlyStructure}
                  onClick={onClick}
                  onRemove={removeItem}
                  onEdit={editItem}
                  depth={depth + 1}
                  index={index}
                  action={action}
                  config={item}
                  fromArray={true}
                ></JsonObject>
              );
            }
            if (item.values.some((v) => v.value.oneofKind === "string")) {
              return (
                <StringValue
                  key={index}
                  label={index}
                  value={item}
                  onlyStructure={onlyStructure}
                  index={index}
                  onRemove={removeItem}
                  onEdit={editItem}
                  fromArray={true}
                  action={action}
                />
              );
            }
            return item.values.map((v, vIndex) => {
              if (v.value.oneofKind === "list") {
                return (
                  <ArrayObject
                    key={`${index}-${vIndex}`}
                    description={description}
                    label={index.toString()}
                    input={v.value.list}
                    onlyStructure={onlyStructure}
                    onClick={onClick}
                    onRemove={removeItem}
                    onEdit={editItem}
                    depth={depth + 1}
                    index={index}
                    action={action}
                  />
                );
              } else if (v.value.oneofKind === "number") {
                return (
                  <NumberValue
                    key={`${index}-${vIndex}`}
                    label={index}
                    value={v}
                    onlyStructure={onlyStructure}
                    index={index}
                    onRemove={removeItem}
                    onEdit={editItem}
                    fromArray={true}
                  />
                );
              } else if (v.value.oneofKind === "bool") {
                return (
                  <BooleanValue
                    key={`${index}-${vIndex}`}
                    label={index}
                    value={v}
                    onlyStructure={onlyStructure}
                    index={index}
                    onRemove={removeItem}
                    onEdit={editItem}
                    fromArray={true}
                  />
                );
              } else if (v.value.oneofKind === "reference") {
                return (
                  <ReferenceValue
                    key={`${index}-${vIndex}`}
                    label={index.toString()}
                    value={v}
                    config={item}
                    index={index}
                    onRemove={removeItem}
                    onEdit={editItem}
                    action={action}
                  />
                );
              }
              return null;
            });
          })}
          {typeSelectModalOpen && (
            <ModalSelectField
              description={description}
              listConfig={input}
              onlyStructure={onlyStructure}
              onClose={() => setTypeSelectModalOpen(false)}
              onChange={onClick}
              excessDepth={depth > MAX_DEPTH}
              action={action}
            />
          )}
          {propertyModalOpen && (
            <PropertyModal
              description={description}
              listConfig={input}
              onlyStructure={onlyStructure}
              onClose={() => setPropertyModalOpen(false)}
              onChange={onClick}
              excessDepth={depth > MAX_DEPTH}
              action={action}
              bodyType={getListBodyType(input)}
              editPropIndex={editPropIndex}
            />
          )}
          {isAddItemIsPossible && (
            <ButtonWrapper>
              <Button
                iconName="plus"
                iconSize="S"
                onClick={addItem}
                styles={buttonStyles}
              >
                {t("automation-form.add-item")}
              </Button>
            </ButtonWrapper>
          )}
        </VerticalLine>
      </S.Root>
    );
  },
);
